/**
 * Onboarding Modal Handler
 * Manages seller onboarding popup for first-time customers
 */

function fetchJsonWithTimeout(url, options = {}, timeoutMs = 4000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
  });
}

class OnboardingModalHandler {
  constructor() {
    this.modal = document.getElementById('onboarding-modal');
    this.form = document.getElementById('onboarding-form');
    this.sellerFieldsContainer = document.getElementById('seller-fields');
    this.createSellerCheckbox = document.getElementById('create-seller-checkbox');
    this.submitButton = document.getElementById('submit-button');
    this.closeButton = document.getElementById('close-button');
    this.errorDiv = document.getElementById('onboarding-error-summary') || document.getElementById('onboarding-error');
    this.closeIconButton = document.querySelector('[aria-label="Close onboarding modal"]');
    this.launcherButton = document.getElementById('onboarding-launcher');
    this.submitDefaultText = this.submitButton?.textContent?.trim() || 'Complete Profile';
    this.hasManualStoreNameUpdate = false;
    this.isProgrammaticStoreNameUpdate = false;

    this.isLoading = false;
    this.customerData = null;
    this.customerContext = null;
    this.isOnboardingComplete = false;

    this.init();
  }

  async init() {
    if (!this.modal || !this.form) return;

    window.__onboardingModalHandler = this;
    this.attachEventListeners();
    this.toggleSellerFields();

    const openedFromQueryFlag = await this.openSellerFlowFromQueryFlag();
    if (openedFromQueryFlag) return;

    await this.loadOnboardingStatus();
  }

  async openSellerFlowFromQueryFlag() {
    const url = new URL(window.location.href);
    const shouldOpenSellerFlow = url.searchParams.get('openSellerOnboarding') === '1';
    if (!shouldOpenSellerFlow) return false;

    url.searchParams.delete('openSellerOnboarding');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

    await this.openSellerFlow();
    return true;
  }

  getShopId() {
    return String(window.gadgetShopId || window.shopId || '').trim();
  }

  getInitialCustomerContext() {
    const customerId = String(window.__CUSTOMER_DATA__?.id || '').trim();
    const shopId = this.getShopId();

    if (!customerId || !shopId) return null;

    return {
      customerId,
      shopId,
      isAuthenticated: true,
    };
  }

  async resolveCustomerContext() {
    const initialContext = this.getInitialCustomerContext();
    if (initialContext) {
      this.customerContext = initialContext;
      return initialContext;
    }

    return this.fetchCustomerContext();
  }

  attachEventListeners() {
    // Form submission
    this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));

    // Close buttons
    this.closeButton?.addEventListener('click', () => this.handleClose());
    this.closeIconButton?.addEventListener('click', () => this.handleClose());

    // Toggle seller fields
    this.createSellerCheckbox?.addEventListener('change', () => this.toggleSellerFields());

    const customerFirstNameInput = document.getElementById('customer-first-name');
    const customerLastNameInput = document.getElementById('customer-last-name');
    customerFirstNameInput?.addEventListener('input', () => this.syncStoreNameFromCustomerName());
    customerLastNameInput?.addEventListener('input', () => this.syncStoreNameFromCustomerName());

    const storeNameInput = document.getElementById('business-name');
    storeNameInput?.addEventListener('input', () => {
      if (this.isProgrammaticStoreNameUpdate) return;
      this.hasManualStoreNameUpdate = true;
    });

    // Persist draft values while user types so refresh does not lose seller intent.
    this.form?.addEventListener('input', () => this.persistDraft());
    this.form?.addEventListener('change', () => this.persistDraft());

    // Floating launcher button
    this.launcherButton?.addEventListener('click', () => this.openFromLauncher());
  }

  toggleSellerFields() {
    if (!this.createSellerCheckbox || !this.sellerFieldsContainer) {
      this.updateSubmitButtonLabel();
      return;
    }

    const isChecked = this.createSellerCheckbox.checked;
    if (isChecked) {
      this.sellerFieldsContainer.classList.add('is-visible');
      this.ensureDefaultStoreName();
    } else {
      this.sellerFieldsContainer.classList.remove('is-visible');
    }

    this.updateSubmitButtonLabel();
  }

  updateSubmitButtonLabel() {
    if (!this.submitButton) return;
    this.submitButton.textContent = this.createSellerCheckbox?.checked ? 'Continue' : 'Complete Profile';
  }

  ensureDefaultStoreName() {
    const storeNameInput = document.getElementById('business-name');
    if (!storeNameInput || storeNameInput.readOnly) return;

    if (this.hasManualStoreNameUpdate) return;

    const currentValue = String(storeNameInput.value || '').trim();
    if (currentValue.length > 0 && !storeNameInput.dataset.autoGenerated) return;

    const defaultStoreName = this.getDefaultStoreName();
    if (!defaultStoreName) return;

    this.isProgrammaticStoreNameUpdate = true;
    storeNameInput.value = defaultStoreName;
    storeNameInput.dataset.autoGenerated = '1';
    this.isProgrammaticStoreNameUpdate = false;
  }

  syncStoreNameFromCustomerName() {
    const storeNameInput = document.getElementById('business-name');
    if (!storeNameInput || storeNameInput.readOnly || this.hasManualStoreNameUpdate) return;

    const existingValue = String(storeNameInput.value || '').trim();
    const isAutoGenerated = storeNameInput.dataset.autoGenerated === '1';
    if (existingValue.length > 0 && !isAutoGenerated) return;

    const nextStoreName = this.getDefaultStoreName();
    if (!nextStoreName) return;

    this.isProgrammaticStoreNameUpdate = true;
    storeNameInput.value = nextStoreName;
    storeNameInput.dataset.autoGenerated = '1';
    this.isProgrammaticStoreNameUpdate = false;
  }

  getDefaultStoreName() {
    const explicitFirstName = String(document.getElementById('customer-first-name')?.value || '').trim();
    const customerFirstName = String(window.__CUSTOMER_DATA__?.firstName || '').trim();

    const firstToken = String(explicitFirstName || customerFirstName || 'My')
      .split(/\s+/)
      .filter(Boolean)[0] || 'My';

    return `${firstToken}'s Billiards Shop`;
  }

  isApp() {
    const token = String(document.documentElement?.getAttribute('data-app-ua-token') || '').trim().toLowerCase();
    return token === 'phjapp';
  }

  updateLauncherVisibility(isVisible) {
    if (!this.launcherButton) return;
    const shouldShow = Boolean(isVisible) && this.isHomepagePath();
    if (shouldShow) {
      this.launcherButton.classList.add('is-visible');
    } else {
      this.launcherButton.classList.remove('is-visible');
    }
  }

  isHomepagePath() {
    const path = String(window.location?.pathname || '/').replace(/\/+$/, '') || '/';
    return path === '/';
  }

  getDraftStorageKey() {
    const customerId = this.customerContext?.customerId || window.__CUSTOMER_DATA__?.id || 'guest';
    return `onboarding:draft:${customerId}`;
  }

  persistDraft() {
    try {
      const payload = {
        createSellerNow: Boolean(this.createSellerCheckbox?.checked),
        accountType: document.getElementById('account-type')?.value || 'personal',
        businessName: document.getElementById('business-name')?.value || '',
        storeName: document.getElementById('business-name')?.value || '',
        addressLine1: document.getElementById('address-line1')?.value || '',
        addressLine2: document.getElementById('address-line2')?.value || '',
        city: document.getElementById('city')?.value || '',
        state: document.getElementById('state')?.value || '',
        postalCode: document.getElementById('postal-code')?.value || '',
        country: document.getElementById('country')?.value || '',
        youtubeUrl: document.getElementById('youtube-url')?.value || '',
        instagramUrl: document.getElementById('instagram-url')?.value || '',
        tiktokUrl: document.getElementById('tiktok-url')?.value || '',
        facebookUrl: document.getElementById('facebook-url')?.value || '',
      };
      localStorage.setItem(this.getDraftStorageKey(), JSON.stringify(payload));
    } catch (error) {
      console.warn('[Onboarding] Failed to persist draft:', error);
    }
  }

  restoreDraft() {
    try {
      const raw = localStorage.getItem(this.getDraftStorageKey());
      if (!raw) return;
      const draft = JSON.parse(raw);

      if (this.createSellerCheckbox) {
        this.createSellerCheckbox.checked = Boolean(draft.createSellerNow);
      }

      const setValue = (id, value) => {
        const el = document.getElementById(id);
        if (el && typeof value === 'string') el.value = value;
      };

      setValue('account-type', draft.accountType || 'personal');
      setValue('business-name', draft.businessName || '');
      setValue('address-line1', draft.addressLine1 || '');
      setValue('address-line2', draft.addressLine2 || '');
      setValue('city', draft.city || '');
      setValue('state', draft.state || '');
      setValue('postal-code', draft.postalCode || '');
      setValue('country', draft.country || '');
      setValue('youtube-url', draft.youtubeUrl || '');
      setValue('instagram-url', draft.instagramUrl || '');
      setValue('tiktok-url', draft.tiktokUrl || '');
      setValue('facebook-url', draft.facebookUrl || '');

      this.toggleSellerFields();
    } catch (error) {
      console.warn('[Onboarding] Failed to restore draft:', error);
    }
  }

  populateSellerInfo(sellerData) {
    if (!sellerData || typeof sellerData !== 'object') return;

    const setValue = (id, value) => {
      const el = document.getElementById(id);
      if (el && typeof value === 'string') el.value = value;
    };

    const hasSellerValues = [
      sellerData.storeName,
      sellerData.businessName,
      sellerData.addressLine1,
      sellerData.addressLine2,
      sellerData.city,
      sellerData.state,
      sellerData.postalCode,
      sellerData.country,
      sellerData.youtubeUrl,
      sellerData.instagramUrl,
      sellerData.tiktokUrl,
      sellerData.facebookUrl,
    ].some((value) => String(value || '').trim().length > 0);

    if (hasSellerValues && this.createSellerCheckbox) {
      this.createSellerCheckbox.checked = true;
    }

    const resolvedStoreName = sellerData.storeName || sellerData.businessName || '';
    setValue('business-name', resolvedStoreName);
    const storeNameInput = document.getElementById('business-name');
    if (storeNameInput && String(resolvedStoreName || '').trim().length > 0) {
      storeNameInput.dataset.autoGenerated = '0';
      this.hasManualStoreNameUpdate = true;
    }
    setValue('account-type', sellerData.accountType || 'personal');

    if (storeNameInput) {
      const isLocked = Boolean(sellerData.isStoreNameLocked);
      storeNameInput.readOnly = isLocked;
      storeNameInput.title = isLocked ? 'Store name is locked after first save' : '';
    }

    setValue('address-line1', sellerData.addressLine1 || '');
    setValue('address-line2', sellerData.addressLine2 || '');
    setValue('city', sellerData.city || '');
    setValue('state', sellerData.state || '');
    setValue('postal-code', sellerData.postalCode || '');
    setValue('country', sellerData.country || '');
    setValue('youtube-url', sellerData.youtubeUrl || '');
    setValue('instagram-url', sellerData.instagramUrl || '');
    setValue('tiktok-url', sellerData.tiktokUrl || '');
    setValue('facebook-url', sellerData.facebookUrl || '');

    this.toggleSellerFields();
  }

  clearDraft() {
    try {
      localStorage.removeItem(this.getDraftStorageKey());
    } catch {
      // no-op
    }
  }

  async openFromLauncher() {
    this.clearValidationErrors();

    if (!this.customerContext) {
      void this.resolveCustomerContext().catch((error) => {
        console.error('[Onboarding] Failed to load customer context from launcher:', error);
      });
    }

    this.showModal();
  }

  async openSellerFlow() {
    this.clearValidationErrors();

    if (!this.customerContext) {
      void this.resolveCustomerContext().catch((error) => {
        console.error('[Onboarding] Failed to load customer context for seller flow:', error);
      });
    }

    this.populateCustomerInfo();
    this.restoreDraft();

    if (this.createSellerCheckbox) {
      this.createSellerCheckbox.checked = true;
    }

    this.toggleSellerFields();
    this.forceCloseAccountPopover();
    this.showModal();
  }

  forceCloseAccountPopover() {
    try {
      const accountEl = document.querySelector('shopify-account');
      if (!accountEl) return;

      if (typeof accountEl.hidePopover === 'function') {
        accountEl.hidePopover();
      }

      const shadow = accountEl.shadowRoot;
      if (!shadow) return;

      const closeButton = shadow.querySelector('button[aria-label*="close" i], .close-button, [part="close-button"]');
      if (closeButton && typeof closeButton.click === 'function') {
        closeButton.click();
      }

      const openPopover = shadow.querySelector('[popover]:popover-open');
      if (openPopover) {
        if (typeof openPopover.hidePopover === 'function') {
          openPopover.hidePopover();
        }
      }

      const openDialog = shadow.querySelector('dialog[open]');
      if (openDialog) {
        if (typeof openDialog.close === 'function') {
          openDialog.close();
        }
      }

      const escapeEvent = new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
      });
      document.dispatchEvent(escapeEvent);
    } catch (error) {
      console.warn('[Onboarding] Could not force-close account popover:', error);
    }
  }

  buildProxyUrl(appName, extraParams = {}) {
    const params = new URLSearchParams();
    params.set('app', appName);

    const customerId = window.__CUSTOMER_DATA__?.id;
    if (customerId) {
      params.set('logged_in_customer_id', String(customerId));
    }

    Object.entries(extraParams || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.set(key, String(value));
      }
    });

    return `/a/app?${params.toString()}`;
  }

  async parseJsonResponse(response, label) {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();

    if (!contentType.includes('application/json')) {
      const bodyText = await response.text();
      const preview = bodyText.slice(0, 200).replace(/\s+/g, ' ').trim();
      throw new Error(
        `[Onboarding] ${label} returned non-JSON (${response.status}) ${response.url}. Preview: ${preview}`
      );
    }

    return response.json();
  }

  async fetchCustomerContext() {
    const response = await fetchJsonWithTimeout(this.buildProxyUrl('get-customer-context'), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }, 3000);

    if (!response.ok) {
      throw new Error(`[Onboarding] Failed customer context lookup: ${response.status}`);
    }

    const context = await this.parseJsonResponse(response, 'customer context');
    this.customerContext = context;
    return context;
  }

  async loadOnboardingStatus() {
    try {
      const context = await this.resolveCustomerContext();
      if (!context?.isAuthenticated || !context?.customerId) {
        this.isOnboardingComplete = false;
        this.updateLauncherVisibility(false);
        this.closeModal();
        return;
      }

      this.customerContext = context;

      // Get onboarding status
      const response = await fetchJsonWithTimeout(this.buildProxyUrl('get-onboarding-status', {
        logged_in_customer_id: context.customerId,
        shop_id: context.shopId,
      }), {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      }, 4000);

      if (!response.ok) {
        console.warn('[Onboarding] Failed to load status:', response.status);
        this.updateLauncherVisibility(false);
        this.closeModal();
        return;
      }

      const data = await this.parseJsonResponse(response, 'onboarding status');

      if (!data.isAuthenticated) {
        console.log('[Onboarding] User not authenticated');
        this.closeModal();
        return;
      }

      if (data.sellerExists) {
        console.log('[Onboarding] Seller already exists; suppressing onboarding modal');
        this.isOnboardingComplete = true;
        this.updateLauncherVisibility(false);
        this.closeModal();
        return;
      }

      // Check if onboarding should be shown
      if (!data.onboardingRequired || data.suppressUntilNextLogin || data.keepClosedPermanently) {
        console.log('[Onboarding] Modal suppressed or already completed');
        this.isOnboardingComplete = !data.onboardingRequired || data.keepClosedPermanently;
        this.updateLauncherVisibility(Boolean(data.onboardingRequired && !data.keepClosedPermanently));
        this.closeModal();
        return;
      }

      const isDevEnvironment = Boolean(data.isDevelopment);
      const isApp = this.isApp();

      if (!isDevEnvironment && !isApp) {
        console.log('[Onboarding] Auto-open suppressed for non-app browser');
        this.isOnboardingComplete = false;
        this.updateLauncherVisibility(true);
        this.closeModal();
        return;
      }

      // Store customer data for form population
      this.customerData = data;
      this.isOnboardingComplete = false;

      // Try to get customer info from Shopify customer object if available
      this.populateCustomerInfo();
      this.populateSellerInfo(data.sellerData);
      this.restoreDraft();

      // Show modal
      this.updateLauncherVisibility(true);
      this.showModal();
    } catch (error) {
      console.error('[Onboarding] Error loading status:', error);
      this.updateLauncherVisibility(false);
      this.closeModal();
    }
  }

  populateCustomerInfo() {
    // Try to get customer info from injected Liquid data
    if (window.__CUSTOMER_DATA__) {
      const customer = window.__CUSTOMER_DATA__;
      document.getElementById('customer-first-name').value = customer.firstName || '';
      document.getElementById('customer-last-name').value = customer.lastName || '';
      if (customer.email) {
        document.getElementById('customer-email').value = customer.email;
      }
      if (customer.phone) {
        document.getElementById('customer-phone').value = customer.phone;
      }
      this.syncStoreNameFromCustomerName();
      return;
    }

    // Fallback: try to get customer info from Shopify checkout object
    if (window.Shopify && window.Shopify.checkout) {
      const customer = window.Shopify.checkout.customer;
      if (customer) {
        document.getElementById('customer-first-name').value = customer.firstName || '';
        document.getElementById('customer-last-name').value = customer.lastName || '';
        document.getElementById('customer-email').value = customer.email || '';
        this.syncStoreNameFromCustomerName();
      }
    }
  }

  showModal() {
    if (this.modal) {
      this.forceCloseAccountPopover();
      this.dismissCompetingOverlays();
      this.toggleSellerFields();
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('onboarding-modal-open');
      document.body.style.overflow = 'hidden';
      this.updateLauncherVisibility(false);

      // Some popovers re-open on focus/animation frames; sweep once more.
      setTimeout(() => {
        this.dismissCompetingOverlays();
      }, 60);
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('onboarding-modal-open');
      document.body.style.overflow = '';
    }
  }

  dismissCompetingOverlays() {
    try {
      const modal = this.modal;
      const shouldIgnore = (node) => {
        if (!node || !modal) return false;
        return node === modal || modal.contains(node);
      };

      const popovers = document.querySelectorAll(':popover-open');
      popovers.forEach((popover) => {
        if (shouldIgnore(popover)) return;
        if (typeof popover.hidePopover === 'function') {
          popover.hidePopover();
        }
      });

      const dialogs = document.querySelectorAll('dialog[open]');
      dialogs.forEach((dialog) => {
        if (shouldIgnore(dialog)) return;
        if (typeof dialog.close === 'function') {
          dialog.close();
        }
      });

      const escapeEvent = new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
      });
      document.dispatchEvent(escapeEvent);
    } catch (error) {
      console.warn('[Onboarding] Failed dismissing competing overlays:', error);
    }
  }

  async handleClose() {
    const keepClosed = Boolean(document.getElementById('keep-closed-checkbox')?.checked);

    // Close immediately for responsive UX, then persist dismissal in the background.
    this.closeModal();
    this.updateLauncherVisibility(!keepClosed);

    // Set suppressUntilNextLogin flag
    void fetch(this.buildProxyUrl('save-onboarding', {
      logged_in_customer_id: this.customerContext?.customerId,
      shop_id: this.customerContext?.shopId,
    }), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        suppressUntilNextLogin: true,
        keepClosed,
      }),
    }).catch(e => console.error('[Onboarding] Error setting suppress flag:', e));
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    if (this.isLoading) return;
    this.isLoading = true;
    this.submitButton.disabled = true;
    this.clearValidationErrors();

    try {
      this.toggleSellerFields();

      if (!this.customerContext) {
        try {
          await this.resolveCustomerContext();
        } catch (error) {
          console.error('[Onboarding] Failed to load customer context for submit:', error);
        }
      }

      // Gather form data
      const formData = this.getFormData();

      // Validate first and last name separately for clearer UX.
      if (!formData.firstName || !formData.lastName) {
        if (!formData.firstName) {
          this.setFieldError('firstName', 'First name is required.');
        }
        if (!formData.lastName) {
          this.setFieldError('lastName', 'Last name is required.');
        }
        this.showError('Please enter your first and last name in the fields above.');
        this.isLoading = false;
        this.submitButton.disabled = false;
        this.updateSubmitButtonLabel();
        return;
      }

      this.submitButton.textContent = this.createSellerCheckbox?.checked
        ? 'Saving seller profile...'
        : 'Saving profile...';

      // Validate required fields for seller account
      if (formData.createSellerNow) {
        const requiredFields = ['businessName', 'addressLine1', 'city', 'state', 'postalCode', 'country'];
        const missingFields = requiredFields.filter(field => !formData[field]);

        if (missingFields.length > 0) {
          const labels = {
            businessName: 'Store Name',
            addressLine1: 'Address',
            city: 'City',
            state: 'State',
            postalCode: 'Zip Code',
            country: 'Country',
          };

          missingFields.forEach((field) => {
            this.setFieldError(field, `${labels[field] || field} is required.`);
          });

          this.showError(`Please fill in required seller fields: ${missingFields.map((field) => labels[field] || field).join(', ')}`);
          this.isLoading = false;
          this.submitButton.disabled = false;
          this.updateSubmitButtonLabel();
          return;
        }
      }

      // Send to save-onboarding endpoint
      const response = await fetch(this.buildProxyUrl('save-onboarding', {
        logged_in_customer_id: this.customerContext?.customerId,
        shop_id: this.customerContext?.shopId,
      }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await this.parseJsonResponse(response, 'save onboarding error').catch(() => ({}));
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      const result = await this.parseJsonResponse(response, 'save onboarding');

      if (!result.success) {
        const backendMessage = [result.message, result.error]
          .filter((item) => String(item || '').trim().length > 0)
          .join(' - ');
        throw new Error(backendMessage || 'Failed to save onboarding');
      }

      if (formData.createSellerNow && !result?.saved?.sellerCreated) {
        throw new Error('Seller account was not created. Please review your business details and try again.');
      }

      // Success: close modal
      this.isOnboardingComplete = true;
      this.clearDraft();
      this.updateLauncherVisibility(false);
      this.closeModal();

      // If seller was created, redirect to seller profile
      if (result.redirectUrl) {
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 500);
      }
    } catch (error) {
      console.error('[Onboarding] Form submission error:', error);
      this.showError(error.message || 'An error occurred. Please try again.');
    } finally {
      this.isLoading = false;
      this.submitButton.disabled = false;
      this.updateSubmitButtonLabel();
    }
  }

  getFormData() {
    const createSellerNow = document.getElementById('create-seller-checkbox').checked;
    const firstName = document.getElementById('customer-first-name').value.trim();
    const lastName = document.getElementById('customer-last-name').value.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    return {
      firstName,
      lastName,
      name: fullName,
      email: document.getElementById('customer-email').value.trim(),
      phone: document.getElementById('customer-phone').value.trim(),
      createSellerNow,
      accountType: createSellerNow ? document.getElementById('account-type').value : undefined,
      businessName: createSellerNow ? document.getElementById('business-name').value.trim() : undefined,
      storeName: createSellerNow ? document.getElementById('business-name').value.trim() : undefined,
      addressLine1: createSellerNow ? document.getElementById('address-line1').value.trim() : undefined,
      addressLine2: createSellerNow ? document.getElementById('address-line2').value.trim() : undefined,
      city: createSellerNow ? document.getElementById('city').value.trim() : undefined,
      state: createSellerNow ? document.getElementById('state').value.trim() : undefined,
      postalCode: createSellerNow ? document.getElementById('postal-code').value.trim() : undefined,
      country: createSellerNow ? document.getElementById('country').value.trim() : undefined,
      youtubeUrl: createSellerNow ? document.getElementById('youtube-url').value.trim() : undefined,
      instagramUrl: createSellerNow ? document.getElementById('instagram-url').value.trim() : undefined,
      tiktokUrl: createSellerNow ? document.getElementById('tiktok-url').value.trim() : undefined,
      facebookUrl: createSellerNow ? document.getElementById('facebook-url').value.trim() : undefined,
      keepClosed: document.getElementById('keep-closed-checkbox').checked,
    };
  }

  showError(message) {
    if (!this.errorDiv) return;
    this.errorDiv.textContent = message;
    this.errorDiv.style.display = 'block';
    this.errorDiv.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  clearValidationErrors() {
    if (this.errorDiv) {
      this.errorDiv.textContent = '';
      this.errorDiv.style.display = 'none';
    }

    this.form?.querySelectorAll('.onboarding-modal__input--error').forEach((input) => {
      input.classList.remove('onboarding-modal__input--error');
    });

    this.form?.querySelectorAll('.onboarding-modal__field-error').forEach((errorNode) => {
      errorNode.remove();
    });
  }

  setFieldError(fieldName, message) {
    const fieldIdMap = {
      firstName: 'customer-first-name',
      lastName: 'customer-last-name',
      name: 'customer-first-name',
      email: 'customer-email',
      phone: 'customer-phone',
      accountType: 'account-type',
      businessName: 'business-name',
      addressLine1: 'address-line1',
      addressLine2: 'address-line2',
      city: 'city',
      state: 'state',
      postalCode: 'postal-code',
      country: 'country',
      youtubeUrl: 'youtube-url',
      instagramUrl: 'instagram-url',
      tiktokUrl: 'tiktok-url',
      facebookUrl: 'facebook-url',
    };

    const fieldId = fieldIdMap[fieldName];
    if (!fieldId) return;

    const input = document.getElementById(fieldId);
    if (!input) return;

    input.classList.add('onboarding-modal__input--error');

    const existing = input.parentElement?.querySelector(`.onboarding-modal__field-error[data-for="${fieldId}"]`);
    if (existing) {
      existing.textContent = message;
      return;
    }

    const error = document.createElement('div');
    error.className = 'onboarding-modal__field-error';
    error.setAttribute('data-for', fieldId);
    error.textContent = message;
    input.insertAdjacentElement('afterend', error);
  }
}

function registerOnboardingGlobalOpeners() {
  window.openOnboardingSellerFlow = async function openOnboardingSellerFlow() {
    const handler = window.__onboardingModalHandler;
    if (!handler || typeof handler.openSellerFlow !== 'function') {
      return false;
    }

    handler.forceCloseAccountPopover();
    await new Promise((resolve) => setTimeout(resolve, 180));
    await handler.openSellerFlow();
    return true;
  };

  window.addEventListener('onboarding:open-seller', () => {
    window.openOnboardingSellerFlow();
  });
}

function initializeOnboardingModal() {
  registerOnboardingGlobalOpeners();
  new OnboardingModalHandler();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeOnboardingModal();
  });
} else {
  initializeOnboardingModal();
}
